
// View / interaction state hooks separated from data hooks (lib/store.ts).
// View + scope + filters are mirrored to the URL so links can be shared.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { Initiative } from "./types";
import { useRoster, useInitiatives } from "./store";
import { getCurrentUser, matchRosterPerson } from "./auth";
import { AUTH_ENABLED } from "./config";
import {
  DEFAULT_WINDOW_START, FISCAL_YEARS, clampWindowStart, fyWindow, monthParam, normalizeQuarterId,
  parseMonthParam, sameMonth,
} from "./constants";
import { type Filters } from "@/components/roadmap/filter-bar";
import { matchesTeamFilter } from "./team-filter";
import { DEFAULT_GROUP_BY, NO_WORKSTREAM, formatGroupParam, loadGroupByPref, parseGroupParam, saveGroupByPref, type GroupBy } from "./group-by";

// The whole portfolio on one canvas, grouped by any of Team > Pod >
// Workstream (see lib/group-by.ts). An initiative appears exactly once in
// every grouping. (The old single-team drill-down is gone; its links map to
// team grouping.)
export type Scope = { zoom: "org"; groupBy: GroupBy };
export type View = "timeline" | "list" | "review";

const FILTER_KEYS: (keyof Filters)[] = ["quarter", "status", "dri", "pillar", "team", "pod", "workstream"];

function parseList(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(",").map(s => s.trim()).filter(Boolean);
}

function readStateFromUrl(params: URLSearchParams): {
  view: View;
  scope: Scope;
  windowStart: Date;
  filters: Filters;
  query: string;
} {
  const viewParam = params.get("view");
  // Small screens default to List — a 375px-wide fiscal-year wall shows a
  // sliver of Q1 and helps nobody. Timeline stays one tap away, and an
  // explicit ?view=timeline link is always honored.
  const smallScreen = typeof window !== "undefined" && window.innerWidth < 768;
  const view: View =
    viewParam === "list" ? "list"
    : viewParam === "review" ? "review"
    : viewParam === "timeline" ? "timeline"
    : smallScreen ? "list"
    : "timeline";
  // Grouping: an explicit ?group= link wins, then this browser's last
  // choice, then the default (Team > Pod > Workstream). A stale
  // ?scope=team drill-down link maps to team lanes.
  const groupBy: GroupBy =
    parseGroupParam(params.get("group"))
    ?? (params.get("scope") === "team" ? { team: true, pod: false, workstream: false } : null)
    ?? loadGroupByPref()
    ?? DEFAULT_GROUP_BY;
  const scope: Scope = { zoom: "org", groupBy };
  // First month of the timeline's rolling window — shareable as ?from=2026-09.
  // Legacy ?fy=FY27 links (from the old FY switcher) open on that year's
  // first month. Anything else opens on the current quarter.
  const fromParam = parseMonthParam(params.get("from"));
  const fyParam = params.get("fy");
  const legacyFy = fyParam && FISCAL_YEARS.some(f => f.fy === fyParam) ? fyWindow(fyParam).start : undefined;
  const windowStart = clampWindowStart(fromParam ?? legacyFy ?? DEFAULT_WINDOW_START);
  const filters: Filters = {
    // Legacy bookmarked links carry plain "Q2" — normalize so they keep working.
    quarter: parseList(params.get("quarter")).map(normalizeQuarterId),
    status: parseList(params.get("status")),
    dri: parseList(params.get("dri")),
    pillar: parseList(params.get("pillar")),
    team: parseList(params.get("team")),
    pod: parseList(params.get("pod")),
    workstream: parseList(params.get("workstream")),
  };
  const query = params.get("q") ?? "";
  return { view, scope, windowStart, filters, query };
}

function writeStateToParams(view: View, scope: Scope, windowStart: Date, filters: Filters, query: string): URLSearchParams {
  const out = new URLSearchParams();
  if (view !== "timeline") out.set("view", view);
  // Always written, so a shared link reproduces the grouping exactly.
  out.set("group", formatGroupParam(scope.groupBy));
  if (!sameMonth(windowStart, DEFAULT_WINDOW_START)) out.set("from", monthParam(windowStart));
  for (const k of FILTER_KEYS) {
    const arr = filters[k];
    if (arr.length > 0) out.set(k, arr.join(","));
  }
  if (query.trim()) out.set("q", query.trim());
  return out;
}

/**
 * useRoadmapView — UI/interaction state for the roadmap page.
 *
 * - Holds view, scope, filters, and active drawer/modal targets.
 * - Mirrors view + scope + filters to the URL so shareable links work.
 * - Returns derived `filtered` initiatives list.
 */
export function useRoadmapView(initiatives: Initiative[]) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Initialize from URL (once, on mount).
  const initial = useMemo(
    () => readStateFromUrl(new URLSearchParams(searchParams.toString())),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [view, setViewState] = useState<View>(initial.view);
  const [scope, setScopeState] = useState<Scope>(initial.scope);
  const [windowStart, setWindowStartState] = useState<Date>(initial.windowStart);
  const [filters, setFiltersState] = useState<Filters>(initial.filters);
  const [query, setQuery] = useState<string>(initial.query);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);

  // Whenever view/scope/window/filters/query change, mirror to the URL.
  const isFirstSync = useRef(true);
  useEffect(() => {
    const params = writeStateToParams(view, scope, windowStart, filters, query);
    const qs = params.toString();
    const search = qs ? `?${qs}` : "";
    if (isFirstSync.current) {
      // Don't replace on initial mount — the URL is already the source.
      isFirstSync.current = false;
      return;
    }
    navigate({ pathname: "/", search }, { replace: true });
  }, [view, scope, windowStart, filters, query, navigate]);

  const setView = useCallback((v: View) => setViewState(v), []);
  const setScope = useCallback((s: Scope) => setScopeState(s), []);
  // Changing the grouping on purpose makes it this browser's default.
  const setGroupBy = useCallback((g: GroupBy) => {
    setScopeState({ zoom: "org", groupBy: g });
    saveGroupByPref(g);
  }, []);
  const setWindowStart = useCallback((d: Date) => setWindowStartState(clampWindowStart(d)), []);
  const setFilters = useCallback((f: Filters) => setFiltersState(f), []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return initiatives.filter(i => {
      if (filters.quarter.length && !filters.quarter.includes(normalizeQuarterId(i.targetQ))) return false;
      if (filters.status.length && !filters.status.includes(i.status)) return false;
      if (filters.dri.length && !filters.dri.includes(i.driId)) return false;
      if (filters.pillar.length && !i.pillarIds.some(p => filters.pillar.includes(p))) return false;
      if (!matchesTeamFilter(filters, i)) return false;
      if (filters.workstream.length) {
        const w = i.workstream?.trim().toLowerCase();
        const hit = filters.workstream.some(f => f === NO_WORKSTREAM ? !w : f.toLowerCase() === w);
        if (!hit) return false;
      }
      if (q && !i.name.toLowerCase().includes(q) && !(i.tldr ?? "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [initiatives, filters, query]);

  const activeInitiative = useMemo(
    () => (activeId ? initiatives.find(i => i.id === activeId) ?? null : null),
    [activeId, initiatives]
  );

  const openDrawer = useCallback((i: Initiative) => setActiveId(i.id), []);
  const closeDrawer = useCallback(() => setActiveId(null), []);
  const openNew = useCallback(() => setShowNew(true), []);
  const closeNew = useCallback(() => setShowNew(false), []);

  return {
    view, setView,
    scope, setScope, setGroupBy,
    windowStart, setWindowStart,
    filters, setFilters,
    query, setQuery,
    activeId, setActiveId,
    showNew, openNew, closeNew,
    filtered,
    activeInitiative,
    openDrawer,
    closeDrawer,
  };
}

/**
 * useDataLayer — combined roster + initiatives store access.
 *
 * Phase 1 backs onto localStorage via lib/store.ts.
 * Phase 2 swaps the underlying implementation to API routes; the surface here
 * stays the same so AppShell doesn't need to change.
 */
export function useDataLayer() {
  const { roster, hydrated: rosterHydrated, loadError: rosterError, reload: reloadRoster, addPerson } = useRoster();
  const { initiatives, hydrated: initsHydrated, loadError: initsError, reload: reloadInits, create, update, archive, restore, postUpdate } = useInitiatives();

  const hydrated = rosterHydrated && initsHydrated;
  // Either load failing leaves the roadmap unusable, so one error screen and
  // one Retry cover both.
  const loadError = initsError ?? rosterError;
  const reload = useCallback(() => {
    if (initsError) reloadInits();
    if (rosterError) reloadRoster();
  }, [initsError, rosterError, reloadInits, reloadRoster]);

  // The signed-in user, resolved to their roster entry. When auth is on we
  // provision that entry from the Entra token once the roster has loaded, so
  // update authorship is real identity rather than a name someone picked.
  // addPerson reuses an existing row on an email (or name) match, so this is
  // idempotent across sessions and users.
  const identity = AUTH_ENABLED ? getCurrentUser() : null;

  useEffect(() => {
    if (!identity || !rosterHydrated) return;
    // Idempotent: addPerson reuses an existing row on an email (or name) match,
    // so this is a no-op on every sign-in after the first.
    addPerson(identity.name, identity.email);
    // addPerson is stable; identity fields are the real inputs.
  }, [identity?.oid, identity?.name, identity?.email, rosterHydrated, addPerson]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolved from the roster by identity, not by the id we generated — see
  // matchRosterPerson. A failed write rolls the row back out of the roster, so
  // currentUser correctly goes null and the UI falls back to manual selection.
  const currentUser = matchRosterPerson(identity, roster);

  return {
    roster,
    initiatives,
    hydrated,
    loadError,
    reload,
    addPerson,
    create,
    update,
    archive,
    restore,
    postUpdate,
    currentUser, // null when auth is off — callers fall back to manual selection
  };
}
